import { useState, FormEvent } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { Lock, Loader2, Check, XCircle, ArrowLeft } from 'lucide-react';
import { setPasswordViaInvite, resetPassword, ApiException } from '../api/client';
import { Karte } from '../components/leitstand';

/**
 * Passwort setzen — eine Seite fuer zwei Wege:
 * - /set-password?token=…   (Einladung fuer neue Benutzer)
 * - /reset-password?token=… (Zuruecksetzen nach "Passwort vergessen")
 *
 * Laeuft vor der Anmeldung, darum die mittige Karte im Leitstand-Stil ohne
 * Rahmen. Der Token steht in der Adresse; ohne ihn zeigt die Seite nur den
 * Hinweis, einen neuen Link anzufordern.
 */
export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get('token');
  const isReset = location.pathname === '/reset-password';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Die beiden Passwörter sind nicht gleich. Bitte noch einmal eingeben.');
      return;
    }

    if (password.length < 8) {
      setError('Das Passwort braucht mindestens 8 Zeichen.');
      return;
    }

    if (!token) {
      setError('Dieser Link ist unvollständig. Bitte einen neuen Link anfordern.');
      return;
    }

    setLoading(true);

    try {
      if (isReset) {
        await resetPassword(token, password);
      } else {
        await setPasswordViaInvite(token, password);
      }
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(
          err.data.error ||
            'Das Passwort lässt sich gerade nicht speichern. Bitte einen neuen Link anfordern.',
        );
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
            wash<b className="font-semibold text-akzent">·</b>ops
          </p>

          <Karte titel="Link ungültig">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-stoer" />
              </div>
              <p className="text-tinte-2 mb-6">
                Dieser Link lässt sich nicht mehr verwenden. Bitte fordern Sie einen neuen Link an.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-tinte-2 transition-colors hover:text-tinte"
              >
                <ArrowLeft className="w-4 h-4" />
                Zur Anmeldung
              </Link>
            </div>
          </Karte>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <Karte titel={isReset ? 'Passwort zurücksetzen' : 'Passwort setzen'}>
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-lauf" />
              </div>
              <p className="text-tinte font-medium mb-2">Passwort gesetzt</p>
              <p className="text-tinte-2 text-sm mb-6">
                Ihr Passwort wurde {isReset ? 'zurückgesetzt' : 'gesetzt'}. Sie können sich jetzt
                anmelden.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full rounded-md border border-akzent bg-akzent px-4 py-2 font-medium text-akzent-satz transition-colors"
              >
                Zur Anmeldung
              </Link>
            </div>
          ) : (
            <>
              <p className="text-tinte-2 text-sm mb-6 text-center">
                {isReset
                  ? 'Geben Sie Ihr neues Passwort ein.'
                  : 'Willkommen! Bitte setzen Sie Ihr Passwort.'}
              </p>

              {error && (
                <div className="mb-4 rounded-md border border-stoer bg-flaeche-2 p-3 text-center text-stoer">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-tinte-2 mb-1">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tinte-3" />
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 rounded-md border border-linie-2 bg-flaeche-2 text-tinte focus:border-akzent focus:outline-none"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-tinte-3 mt-1">Mindestens 8 Zeichen</p>
                </div>

                <div>
                  <label
                    htmlFor="confirm-password"
                    className="block text-sm font-medium text-tinte-2 mb-1"
                  >
                    Passwort wiederholen
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tinte-3" />
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 rounded-md border border-linie-2 bg-flaeche-2 text-tinte focus:border-akzent focus:outline-none"
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full rounded-md border border-akzent bg-akzent px-4 py-2 font-medium text-akzent-satz transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird gespeichert...
                    </span>
                  ) : (
                    'Passwort setzen'
                  )}
                </button>
              </form>
            </>
          )}
        </Karte>
      </div>
    </div>
  );
}
